import axios from "axios";
import { useEffect, useState, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Navbar,
  Nav,
  NavDropdown,
  Button,
  Container,
  Offcanvas,
} from "react-bootstrap";
import { FaHotel, FaSun, FaMoon, FaSignOutAlt, FaBell } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "../css/Header.css";
import { useNotification } from "../context/NotificationContext";
import { CompanyContext } from "../context/CompanyContext";

const Header = ({ darkMode, toggleDarkMode }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logoutUser } = useContext(AuthContext);
  const { notificationCount } = useNotification();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  const switchLanguage = () => {
    const newLang = i18n.language === "ID" ? "EN" : "ID";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  };

  const getUserInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    let initials = "";
    for (let i = 0; i < words.length && i < 3; i++) {
      if (words[i].length > 0) {
        initials += words[i][0].toUpperCase();
      }
    }
    return initials;
  };

  const userInitials = user ? getUserInitials(user.name) : "";
  const toggleUserMenu = () => setShowUserMenu((prev) => !prev);
  const isFacilitiesActive = location.pathname.startsWith("/facility");
  const { company } = useContext(CompanyContext);

  const handleLogout = () => {
    logoutUser();
    navigate("/login");
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!localStorage.getItem("token")) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_APP_URL}/notifications`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-Platform": process.env.REACT_APP_X_PLATFORM,
              "API-Key": process.env.REACT_APP_API_KEY,
              "X-API-Version": process.env.REACT_APP_X_API_VERSION,
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            withCredentials: true,
          },
        );
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };
    fetchNotifications();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setShowUserMenu(false);
  }, [location]);

  return (
    <Navbar
      expand="lg"
      variant="dark"
      className={`shadow-sm fixed-top navbar-custom ${
        darkMode ? "bg-dark" : "bg-success"
      }`}
    >
      <Container>
        <Navbar.Brand
          as={Link}
          to="/"
          className="fw-bold text-white d-flex align-items-center brand-container"
        >
          <img
            src="/assets/brand/logo.png"
            alt="Hotel Logo"
            className="logo-img"
          />
          <div className="d-flex flex-column ms-2 brand-text">
            <span className="brand-title">
              {" "}
              {company ? company.name : "Loading..."}
            </span>
            <small className="brand-subtitle">Hotel</small>
          </div>
        </Navbar.Brand>
        <div className="d-flex d-lg-none align-items-center ms-auto mobile-header-right">
          {user ? (
            <>
              <div
                className="mx-auto user-initials"
                style={{ backgroundColor: "#ffcc00", color: "#000" }}
                ref={userMenuRef}
                onClick={toggleUserMenu}
              >
                {userInitials}
              </div>
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="user-menu-header">{user.name}</div>
                  <div className="user-menu-item" onClick={handleLogout}>
                    <FaSignOutAlt className="me-2" /> {t("logout")}
                  </div>
                </div>
              )}
              <Button
                variant="link"
                className="text-white position-relative notification-btn"
              >
                <FaBell />
                {notificationCount > 0 && (
                  <span className="notification-badge">
                    {notificationCount}
                  </span>
                )}
              </Button>
            </>
          ) : (
            <Button
              variant="light"
              className="ms-3"
              onClick={() => navigate("/login")}
            >
              {t("login")}
            </Button>
          )}

          <Navbar.Toggle
            aria-controls="offcanvasNavbar"
            className="border-0 ms-2"
          />
        </div>
        <Navbar.Collapse id="basic-navbar-nav" className="d-none d-lg-flex">
          <Nav className="ms-auto align-items-center">
            <Nav.Link
              as={Link}
              to="/"
              className={`nav-link ${
                location.pathname === "/" ? "active" : ""
              }`}
            >
              {t("home")}
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/contact"
              className={`nav-link ${
                location.pathname === "/contact" ? "active" : ""
              }`}
            >
              {t("contact")}
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/about-us"
              className={`nav-link ${
                location.pathname === "/about-us" ? "active" : ""
              }`}
            >
              {t("aboutUs")}
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/blog"
              className={`nav-link ${
                location.pathname === "/blog" ||
                location.pathname.startsWith("/blog/")
                  ? "active"
                  : ""
              }`}
            >
              {t("blog")}
            </Nav.Link>
            <NavDropdown
              title={t("facilities")}
              id="fasilitas-dropdown"
              className={`nav-link ${
                location.pathname.startsWith("/facility") ? "active" : ""
              }`}
            >
              <NavDropdown.Item
                as={Link}
                to="/facility/swimming-pool"
                className={
                  location.pathname === "/facility/swimming-pool"
                    ? "active"
                    : ""
                }
              >
                {t("swimmingPool")}
              </NavDropdown.Item>
            </NavDropdown>
            {user && (
              <Nav.Link
                as={Link}
                to="/dashboard"
                className={`nav-link ${
                  location.pathname === "/dashboard" ? "active" : ""
                }`}
              >
                {t("Dashboard")}
              </Nav.Link>
            )}

            {user && (
              <>
                <div
                  className="user-menu-container ms-3 d-none d-lg-block"
                  ref={userMenuRef}
                >
                  <div
                    className="user-initials"
                    style={{ backgroundColor: "#ffcc00", color: "#000" }}
                    onClick={toggleUserMenu}
                  >
                    {userInitials}
                  </div>
                  {showUserMenu && (
                    <div className="user-menu-dropdown">
                      <div className="user-menu-header">{user.name}</div>
                      <div className="user-menu-item" onClick={handleLogout}>
                        <FaSignOutAlt className="me-2" /> {t("logout")}
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  variant="link"
                  className="text-white position-relative notification-btn d-none d-lg-flex"
                  onClick={() => navigate("/dashboard/notifications")}
                >
                  <FaBell />
                  {notificationCount > 0 && (
                    <span className="notification-badge">
                      {notificationCount}
                    </span>
                  )}
                </Button>
              </>
            )}

            {!user && (
              <Button
                variant="light"
                className="ms-3 d-none d-lg-block"
                onClick={() => navigate("/login")}
              >
                {t("login")}
              </Button>
            )}

            <Button
              variant="outline-light"
              className="ms-3 d-none d-lg-block"
              onClick={switchLanguage}
            >
              <img
                src={
                  i18n.language === "ID"
                    ? "/assets/flags/id.png"
                    : "/assets/flags/en.png"
                }
                alt={i18n.language === "ID" ? "Indonesia" : "English"}
                style={{ width: "24px", height: "24px" }}
                className="me-2"
              />
              {i18n.language === "ID" ? "Bahasa Indonesia" : "English"}
            </Button>
          </Nav>
        </Navbar.Collapse>
        <Navbar.Offcanvas
          id="offcanvasNavbar"
          aria-labelledby="offcanvasNavbarLabel"
          placement="end"
          className="d-lg-none"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title id="offcanvasNavbarLabel">
              Selengkapnya
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <div className="offcanvas-item">
              <Button
                variant="outline-secondary"
                className="d-flex align-items-center"
                onClick={switchLanguage}
              >
                <img
                  src={
                    i18n.language === "ID"
                      ? "/assets/flags/id.png"
                      : "/assets/flags/en.png"
                  }
                  alt={i18n.language === "ID" ? "Indonesia" : "English"}
                  style={{ width: "24px", height: "24px" }}
                  className="me-2"
                />
                {i18n.language === "ID" ? "Bahasa Indonesia" : "English"}
              </Button>
            </div>
            <div className="offcanvas-item">
              <Nav.Link
                as={Link}
                to="/"
                className={`nav-link ${
                  location.pathname === "/" ? "active" : ""
                }`}
              >
                {t("home")}
              </Nav.Link>
            </div>

            <div className="offcanvas-item">
              <Nav.Link
                as={Link}
                to="/contact"
                className={`nav-link ${
                  location.pathname === "/contact" ? "active" : ""
                }`}
              >
                {t("contact")}
              </Nav.Link>
            </div>

            <div className="offcanvas-item">
              <Nav.Link
                as={Link}
                to="/about-us"
                className={`nav-link ${
                  location.pathname === "/about-us" ? "active" : ""
                }`}
              >
                {t("aboutUs")}
              </Nav.Link>
            </div>

            <div className="offcanvas-item">
              <Nav.Link
                as={Link}
                to="/blog"
                className={`nav-link ${
                  location.pathname === "/blog" ? "active" : ""
                }`}
              >
                {t("blog")}
              </Nav.Link>
            </div>

            <div className="offcanvas-item">
              <Nav.Link
                as={Link}
                to="/facility/swimming-pool"
                className={`nav-link ${
                  location.pathname === "/facility/swimming-pool"
                    ? "active"
                    : ""
                }`}
              >
                {t("facilities")}
              </Nav.Link>
            </div>
            <div className="offcanvas-item">
              {user && (
                <Nav.Link
                  as={Link}
                  to="/dashboard"
                  className={`nav-link ${
                    location.pathname === "/dashboard" ? "active" : ""
                  }`}
                >
                  {t("Dashboard")}
                </Nav.Link>
              )}
            </div>

            <hr />
            <div className="fw-bold mb-2">Bantuan dan dukungan</div>
            <div className="offcanvas-item">Hubungi Customer Service</div>
            <div className="offcanvas-item">Masalah mitra</div>
          </Offcanvas.Body>
        </Navbar.Offcanvas>
      </Container>
    </Navbar>
  );
};

export default Header;
